import {type} from 'arktype'
import {format} from 'date-fns'

import {isReadableError} from '@/lib/api/ReadableError'
import throwIfError from '@/lib/api/throwIfError'
import {
  isStateProvinceRequiredCountry,
  MEDICAL_EQUIPMENT_MAP,
  MOBILITY_ASSISTANCE_MAP,
  MOBILITY_DEVICES_MAP,
  SPECIAL_ASSISTANCE_MAP,
} from '@/lib/flight/constants'
import {
  type Airport,
  Booking,
  Bookings,
  type FlightBooking,
  FlightDetails,
  FlightPayment,
  FlightResult,
} from '@/lib/flight/flightModel'
import {
  BookingType,
  Gender,
  MobilityAssistanceType,
  PassengerType,
  TravelClass,
} from '@/lib/flight/types'
import {type AssistanceInformation} from '@/lib/flight/types'
import {getGeoLocation} from '@/lib/locale/geoLocaltion'
import {localeUnderscoreToHyphen, type SupportedLocales} from '@/lib/locale/locale'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import {logError} from '@/utils/logger'
import filteredArray from '@/utils/schema/filteredArray'

const NON_ERROR_API_CODES = ['ICA0000']

export const Airport2 = type({
  airport_code: 'string',
  airport_name: 'string',
})

export type Airport2 = typeof Airport2.infer

export const Airport2s = filteredArray(Airport2)
export type Airport2s = typeof Airport2s.infer

export async function getAirports({
  q,
  signal,
}: {
  q: string
  signal?: AbortSignal
}): Promise<Airport2s> {
  return call
    .get(`/api/model/airport_typeahead/?prefix=${q}`, {signal})
    .then(res => {
      return Airport2s(res.data)
    })
    .catch(err => {
      if (shouldLogError(err)) {
        logError(err)
      }
      return []
    })
}

export interface FlightFilter {
  // NOTE: commented out filters are not supported by the API yet or easier to do in client side
  // stops: StopsFilter
  // airlines?: {
  //   type: 'exclude' | 'include'
  //   value: string[]
  // }
  // exclude_conns?: string[]
  // layover_duration: {
  //   min: number
  //   max: number
  // }
  bags: number
  // maxPrice?: number
  // emissions?: 1
  // outboundTimesFilter?: TimesFilter
  // returnTimesFilter?: TimesFilter
  // maxDuration?: number
}

export async function searchFlights({
  from,
  to,
  departureDate,
  returnDate,
  mode,
  travelClass,
  adults,
  children,
  infantsOnLap,
  infantsInSeat,
  filter,
  signal,
  locale,
  token,
}: {
  from: Airport['id']
  to: Airport['id']
  departureDate: Date
  returnDate?: Date | undefined
  mode?: BookingType | undefined
  travelClass?: TravelClass | undefined
  adults?: number | undefined
  children?: number | undefined
  infantsOnLap?: number | undefined
  infantsInSeat?: number | undefined
  signal?: AbortSignal
  locale: SupportedLocales
  token?: string | undefined
  filter?: FlightFilter
}) {
  const bookingType = (() => {
    switch (mode) {
      case BookingType.RoundTrip:
        return 1
      case BookingType.OneWay:
        return 2
      case BookingType.MultiCity:
        return 3
      case undefined:
        return 1
    }
  })()

  const travel_class = (() => {
    switch (travelClass) {
      case TravelClass.Economy:
        return 1
      case TravelClass.PremiumEconomy:
        return 2
      case TravelClass.Business:
        return 3
      case TravelClass.FirstClass:
        return 4
      case undefined:
        return 1
    }
  })()

  // const stops = (() => {
  //   switch (filter?.stops) {
  //     case StopsFilter.NonStop:
  //       return 1
  //     case StopsFilter.OneStop:
  //       return 2
  //     case StopsFilter.TwoStops:
  //       return 3
  //     case StopsFilter.Any:
  //     case undefined:
  //       return 0
  //   }
  // })()

  const bags = filter?.bags ?? 0
  // const maxPrice = filter?.maxPrice ?? undefined
  // const outboundTimes = filter?.outboundTimesFilter
  //   ? timesFilterToRawTimesFilter(filter.outboundTimesFilter)
  //   : undefined
  // const returnTimes =
  //   filter?.returnTimesFilter && mode === BookingType.RoundTrip
  //     ? timesFilterToRawTimesFilter(filter.returnTimesFilter)
  //     : undefined
  // const maxDuration = filter?.maxDuration ?? undefined
  return call
    .post(
      '/api/model/flight_search_v2/',
      {
        localization: {
          locale,
          geo_location: getGeoLocation(),
        },
        data: {
          type: bookingType,
          departure_token: token,
          departure_id: from,
          arrival_id: to,
          outbound_date: departureDate.toISOString().split('T')[0],
          return_date:
            mode === BookingType.RoundTrip ? returnDate?.toISOString().split('T')[0] : undefined,
          travel_class,
          adults,
          children,
          infants_in_seat: infantsInSeat,
          infants_on_lap: infantsOnLap,
          // stops,
          bags,
          // max_price: maxPrice,
          // outbound_times: outboundTimes,
          // return_times: returnTimes,
          // max_duration: maxDuration,
        },
      },
      {signal},
    )
    .then(res => {
      return FlightResult(res.data)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        logError(e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.message, e)
      }

      throw new UnexpectedError(e)
    })
}

export async function getFlightDetails({
  flightId,
  signal,
  locale,
}: {
  flightId: string
  signal?: AbortSignal
  locale: SupportedLocales
}): Promise<FlightDetails | false> {
  return call
    .post(
      '/api/model/flight/',
      {
        localization: {
          locale,
          geo_location: getGeoLocation(),
        },
        data: {flight_id: flightId},
      },
      {signal},
    )
    .then(res => {
      return FlightDetails(res.data)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        logError(e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.message, e)
      }

      throw new UnexpectedError(e)
    })
}

const MAP_PASSENGER_TYPE = {
  [PassengerType.Adult]: 'adult',
  [PassengerType.Child]: 'child',
  [PassengerType.InfantInSeat]: 'infant_in_seat',
  [PassengerType.InfantOnLap]: 'infant_on_lap',
}

export async function bookFlight({
  isAuthenticated,
  data,
  flightId,
  optionIdx,
  returnHref,
  signal,
  locale,
  assistance,
  applyRewards,
  confirmationToken,
  email,
  coupon,
  roundUp,
}: {
  isAuthenticated: boolean
  email: string
  confirmationToken: string
  data: FlightBooking
  flightId: string
  optionIdx: number
  returnHref: string
  signal?: AbortSignal
  locale: SupportedLocales
  assistance: AssistanceInformation
  applyRewards: boolean
  coupon: string
  roundUp: boolean
}) {
  const transformedData = {
    confirmationTokenId: confirmationToken,
    email,
    checkoutType: 'NO_OP',
    flightId,
    optionIdx,
    passengers: data.passengers.map(passenger => ({
      firstName: passenger.firstName,
      middleName: passenger.middleName,
      lastName: passenger.lastName,
      dateOfBirth: format(passenger.dateOfBirth, 'yyyy-MM-dd'),
      gender: passenger.gender === Gender.Male ? 'MALE' : 'FEMALE',
      country: passenger.country,
      stateProvince: isStateProvinceRequiredCountry(passenger.country)
        ? passenger.stateProvince
        : '',
      type: MAP_PASSENGER_TYPE[passenger.type],
    })),
    contactInfo: {
      firstName: data.contact.firstName,
      lastName: data.contact.lastName,
      middleName: data.contact.middleName,
      phoneNumber: data.contact.phone,
      email: data.contact.email,
    },
    returnUrl: location.origin + returnHref,
    useWallet: applyRewards,
    roundUp,
    code: coupon,
  }

  if (assistance.needAssistance) {
    const assistanceData: Record<string, unknown> = {}
    if (assistance.mobilityAssistance) {
      if (assistance.mobilityAssistanceType === MobilityAssistanceType.PersonalWheelchair) {
        assistanceData.Mobility = [
          `${MOBILITY_ASSISTANCE_MAP[assistance.mobilityAssistanceType]} ## ${
            MOBILITY_DEVICES_MAP[assistance.mobilityDevice]
          }`,
        ]
      } else {
        assistanceData.Mobility = [MOBILITY_ASSISTANCE_MAP[assistance.mobilityAssistanceType]]
      }
    }

    if (assistance.specialAssistance) {
      assistanceData['Special assistance needed at the airport'] = [
        SPECIAL_ASSISTANCE_MAP[assistance.specialAssistanceType],
      ]
    }

    if (assistance.serviceAnimal) {
      assistanceData['Traveling with a service animal'] = true
    }

    if (assistance.medicalEquipment) {
      assistanceData['Electrical medical equipment'] = [
        MEDICAL_EQUIPMENT_MAP[assistance.medicalEquipmentType],
      ]
    }

    // @ts-expect-error -- assistance is not in the schema
    transformedData.assistance = assistanceData
  }

  console.log(transformedData)

  const url = isAuthenticated
    ? '/api/checkout/v1/payments/payment-intents-for-booking'
    : '/api/checkout/pub/v1/payments/payment-intents-for-booking'

  return call
    .post(url, transformedData, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- safe
      const dataData = res.data.data as unknown

      console.log(dataData)

      return FlightPayment(dataData)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        logError(e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.message, e)
      }

      throw new UnexpectedError(e)
    })
}

export async function expireBooking({
  bookingId,
  signal,
}: {
  bookingId: string
  signal?: AbortSignal
}) {
  return call.patch(`/api/checkout/v1/bookings/${bookingId}/expire`, {}, {signal})
}

export async function confirmBooking({
  bookingId,
  signal,
}: {
  bookingId: string
  signal?: AbortSignal
}) {
  return call.patch(`/api/checkout/v1/bookings/${bookingId}/confirm`, {}, {signal})
}

// -----------------------------------------------------------------------------

export async function getBooking({bookingId, signal}: {bookingId: string; signal?: AbortSignal}) {
  return call.get(`/api/checkout/v1/bookings/${bookingId}`, {signal}).then(res => {
    throwIfError(res, NON_ERROR_API_CODES)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: fix this
    return Booking(res.data.data)
  })
}

export async function getBookings({
  signal,
  locale,
}: {
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .get('/api/checkout/v1/bookings', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: fix this
      const records = res.data.data?.records

      if (Array.isArray(records)) return Bookings(records)

      return []
    })
}
